import {
  Box,
  Divider,
  Modal,
  ModalContent,
  ModalOverlay,
  Text
} from '@chakra-ui/react';
import React, { FC } from 'react';
import { useToggle } from 'react-use';
import { AddLiquiditySectionTitle } from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquiditySectionTitle';

import { SubmitButton } from '@app/components/SubmitButton';
import { Currency } from '@cryptoalgebra/kim-sdk';
import { Web3Button } from '@thirdweb-dev/react';
import clsx from 'clsx';
import s from '@app/screens/airdrop/ClaimsList/ClaimsList.module.scss';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import { useConfig } from '@app/config';
import { IDerivedLiquidityByIchiInfo } from '@app/state/liquidityByIchiStore';
import { DepositToIchiWithKpNFTButton } from '@app/screens/liquidity/AddLiquidityV3/components/ichi/DepositToIchiWithKpNFTButton';
import { TokensTitle } from '@app/screens/liquidity/AddLiquidityV3/components/TokensTitle';
import { LockSection } from '../LockSection/LockSection';
import { InfoRow } from '@app/screens/liquidity/AddLiquidityV3/components/InfoRow';
import { formatNumber } from '@app/helpers/format';
import { useFarmBaseApr } from '@app/hooks/plugins/useFarmBaseApr';
import {
  getMarketMakerCampaign,
  useGetMarketMakerCampaign
} from '@app/hooks/merkl/useGetMarketMakerCampaign';
import { useMarketMakerPools } from '@app/hooks/merkl/useMarketMakerPools';
import { Campaign } from '@app/hooks/merkl/useMerklCampaigns';
import { getMarketMakerParams } from '@app/hooks/pools/usePreparedPoolsRows';
import { MetromCampaign } from '@app/hooks/metrom/useMetromCampaigns';
import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';

interface Props {
  currencyA: Currency | undefined;
  currencyB: Currency | undefined;
  mintInfo: IDerivedLiquidityByIchiInfo;
}

export const CreateIchiPositionWithKpNft: FC<Props> = ({
  mintInfo,
  currencyA,
  currencyB
}) => {
  const config = useConfig();
  const [open, toggleOpen] = useToggle(false);
  const { vaultDetails, vault } = mintInfo;
  const {
    actions: { setStep }
  } = useLiquidityWizardState();

  const { farmBaseApr, lockBonusApr, totalApr } = useFarmBaseApr(
    mintInfo.nftPoolAddress,
    mintInfo.vaultDetails?.tvl,
    mintInfo.vaultDetails?.apr,
    mintInfo.lockDuration
  );

  const isValidVaultSelected =
    (mintInfo.vault?.token0.id.toLowerCase() ===
      currencyA?.wrapped.address.toLowerCase() &&
      mintInfo.vault?.token1.id.toLowerCase() ===
        currencyB?.wrapped.address.toLowerCase()) ||
    (mintInfo.vault?.token0.id.toLowerCase() ===
      currencyB?.wrapped.address.toLowerCase() &&
      mintInfo.vault?.token1.id.toLowerCase() ===
        currencyA?.wrapped.address.toLowerCase());

  const marketMakerPools = useMarketMakerPools();

  const { marketMaker } = getMarketMakerParams(
    marketMakerPools,
    vaultDetails?.depositToken.contractAddress,
    vaultDetails?.pairedToken.contractAddress
  );

  const merklAPR =
    marketMaker?.merkl?.reduce((acc: number, curr: Campaign) => {
      return acc + curr.apr;
    }, 0) ?? 0;

  const metromAPR =
    marketMaker?.metrom?.reduce((acc: number, curr: MetromCampaign) => {
      return curr.apr ? acc + +curr.apr : acc;
    }, 0) ?? 0;

  const _totalApr = Number(totalApr ?? 0) + merklAPR + metromAPR;

  function getSubmitLabel() {
    if (!isValidVaultSelected) {
      return 'Select vault';
    }

    if (!mintInfo.amount) {
      return 'Enter deposit amount';
    }

    if (mintInfo.isBalanceExceeded) {
      return 'Deposit token balance exceeded';
    }

    return 'Create position';
  }

  return (
    <>
      <SubmitButton
        isLoading={false}
        label={getSubmitLabel()}
        onClick={toggleOpen}
        disabled={
          mintInfo.isBalanceExceeded ||
          !mintInfo.amount ||
          !isValidVaultSelected
        }
      />
      <Modal
        isOpen={open}
        onClose={() => {
          toggleOpen(false);
        }}
        isCentered
        motionPreset="slideInBottom"
        size="xs"
      >
        <ModalOverlay />
        <ModalContent
          sx={{ maxWidth: ['100vw', '586px'], padding: ['0', '24px'] }}
        >
          <AddLiquiditySectionTitle
            isCompleted={false}
            label="Create position"
            hideStatus
          />
          <Divider
            sx={{
              width: '100%',
              background: 'neutral.500',
              height: '1px',
              my: 3
            }}
          />
          <TokensTitle
            token0Symbol={vaultDetails?.depositToken.symbol ?? ''}
            token1Symbol={vaultDetails?.pairedToken.symbol ?? ''}
            address={vaultDetails?.vaultId ?? ''}
          />

          <LockSection mintInfo={mintInfo} />

          <AddLiquiditySectionTitle
            isCompleted={false}
            label="Estimates"
            hideStatus
          />

          <Box p="0 14px">
            <InfoRow
              label="Total APR"
              value={`${
                totalApr !== undefined ? formatNumber(_totalApr, 2) : 0
              }%`}
            />
            <Divider
              sx={{
                margin: '8px 0',
                background: 'gray.11',
                height: '1px',
                width: '100%'
              }}
            />
            <InfoRow
              label="Fee APR"
              value={`${formatNumber(mintInfo.vaultDetails?.apr ?? 0, 2)}%`}
            />
            <InfoRow
              label={
                <Text
                  fontSize="14px"
                  fontWeight={400}
                  lineHeight="26px"
                  color="warning.100"
                >
                  Market Maker APR
                </Text>
              }
              value={`${formatNumber(merklAPR + metromAPR ?? 0, 2)}%`}
            />
            <InfoRow
              label="Farm Base APR"
              value={`${formatNumber(farmBaseApr, 2)}%`}
            />
            <InfoRow
              label="Lock Bonus APR"
              value={`${lockBonusApr ? formatNumber(lockBonusApr, 2) : 0}%`}
            />
          </Box>

          {mintInfo.nftPoolAddress === null ? (
            <>
              <Alert
                status="warning"
                display="flex"
                border="1px solid"
                borderColor="neutral.700"
                my="16px"
                borderRadius="10px"
                py="10px"
                px="12px"
              >
                <AlertIcon w="16px" h="16px" mr="8px" color="neutral.400" />
                <AlertDescription color="neutral.400">
                  You are the very first kpNFT minter for this asset! You will
                  need to initialize kpNFT contract first.
                </AlertDescription>
              </Alert>

              <Web3Button
                className={clsx(s.claimBtn, s.outlined)}
                contractAddress={config?.CONTRACTS.NFT_POOL_FACTORY!}
                action={async contract =>
                  contract.call('createPool', [mintInfo.vaultDetails?.vaultId])
                }
                onError={error => {
                  console.error(error);
                  showErrorToast('Failed to initialize pool');
                }}
                onSuccess={async () => {
                  showSuccessToast('Successfully initialized NFT pool');
                  await mintInfo.getPoolAddress();
                }}
                isDisabled={false}
              >
                Initialize NFT pool
              </Web3Button>
            </>
          ) : (
            <>
              <DepositToIchiWithKpNFTButton
                mintInfo={mintInfo}
                onComplete={() => {
                  toggleOpen(false);

                  setStep(2);
                }}
              />
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
